import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { auth } from '../lib/firebase';
import IndexMark from '@/components/index/IndexMark';
import { cn } from '@/lib/utils';
import { useUserRoles } from '@/hooks/useUserRoles';

interface LandingNavbarProps {
  variant?: "light" | "dark";
  /** Transparent bar over video heroes (landing page). */
  overlay?: boolean;
}

const LandingNavbar: React.FC<LandingNavbarProps> = ({ variant = "dark", overlay = false }) => {
  const isDark = variant === "dark";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = !!auth.currentUser;
  const { isReviewerOnly } = useUserRoles();
  const homeTo = isReviewerOnly ? '/reviewer' : isAuthenticated ? '/home' : '/';
  const userDisplay = isAuthenticated && auth.currentUser
    ? (auth.currentUser.email || auth.currentUser.displayName || 'User')
    : '';
  return (
    <header
      className={
        overlay
          ? "fixed top-0 left-0 right-0 z-50 bg-transparent border-b border-white/10"
          : isDark
            ? "fixed top-0 left-0 right-0 z-50 bg-black/95 border-b border-[#2a2a2a] backdrop-blur-sm"
            : "fixed top-0 left-0 right-0 z-50 bg-beige border-b border-border-light"
      }
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to={homeTo}
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <IndexMark theme={isDark ? 'dark' : 'light'} />
          </Link>

          {/* Desktop Navigation: Preferences | (Login + Sign up) or (userId + role) */}
          <nav className="hidden md:flex items-center space-x-8">
            {isAuthenticated && !isReviewerOnly && (
              <Link
                to="/home"
                className={
                  isDark
                    ? "text-white/70 hover:text-white text-xs font-sans font-medium uppercase tracking-wider transition-colors"
                    : "text-text-dark hover:opacity-70 text-sm font-sans font-medium transition-opacity"
                }
              >
                HOME
              </Link>
            )}
            {!isReviewerOnly && (
            <Link
              to="/preferences"
              className={
                isDark
                  ? "text-white/70 hover:text-white text-xs font-sans font-medium uppercase tracking-wider transition-colors"
                  : "text-text-dark hover:opacity-70 text-sm font-sans font-medium transition-opacity"
              }
            >
              PREFERENCES
            </Link>
            )}
            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                {userDisplay && (
                  <span
                    className={
                      isDark
                        ? "text-sm font-sans text-white/70 truncate max-w-[180px]"
                        : "text-sm font-sans text-text-dark opacity-90 truncate max-w-[180px]"
                    }
                    title={userDisplay}
                  >
                    {userDisplay}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate('/login')}
                  variant="indexOutline"
                  className={cn(
                    'font-sans font-medium px-6 py-2',
                    !isDark && 'border-gold-button text-gold-button',
                  )}
                >
                  Login
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  variant="indexPrimary"
                  className={cn(!isDark && 'bg-gold-button text-white hover:bg-gold-button-dark')}
                >
                  Sign up
                </Button>
              </div>
            )}
          </nav>

          {/* Mobile Navigation Toggle */}
          <button
            className={
              isDark
                ? "md:hidden p-2 rounded-sm hover:bg-[#1a1a1a] transition-colors text-white"
                : "md:hidden p-2 rounded-sm hover:bg-beige-dark transition-colors text-text-dark"
            }
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div
            className={
              isDark
                ? "md:hidden mt-4 pb-4 border-t border-[#2a2a2a] pt-4"
                : "md:hidden mt-4 pb-4 border-t border-border-light pt-4"
            }
          >
            <nav className="flex flex-col space-y-4">
              {isAuthenticated && !isReviewerOnly && (
                <Link
                  to="/home"
                  className={
                    isDark
                      ? "text-white/70 hover:text-white text-sm font-sans font-medium transition-colors"
                      : "text-text-dark hover:opacity-70 text-sm font-sans font-medium transition-opacity"
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  HOME
                </Link>
              )}
              {!isReviewerOnly && (
              <Link
                to="/preferences"
                className={
                  isDark
                    ? "text-white/70 hover:text-white text-sm font-sans font-medium transition-colors"
                    : "text-text-dark hover:opacity-70 text-sm font-sans font-medium transition-opacity"
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                PREFERENCES
              </Link>
              )}
              {isAuthenticated ? (
                <div
                  className={
                    isDark
                      ? "flex flex-col gap-2 text-sm font-sans text-white/70"
                      : "flex flex-col gap-2 text-sm font-sans text-text-dark"
                  }
                >
                  {userDisplay && <span className="truncate">{userDisplay}</span>}
                </div>
              ) : (
                <div className="flex flex-col gap-2 w-full">
                  <Button
                    onClick={() => {
                      navigate('/login');
                      setMobileMenuOpen(false);
                    }}
                    variant="indexOutline"
                    className={cn('w-full', !isDark && 'border-gold-button text-gold-button')}
                  >
                    Login
                  </Button>
                  <Button
                    onClick={() => {
                      navigate('/register');
                      setMobileMenuOpen(false);
                    }}
                    variant="indexPrimary"
                    className={cn('w-full', !isDark && 'bg-gold-button text-white hover:bg-gold-button-dark')}
                  >
                    Sign up
                  </Button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default LandingNavbar;
